import { useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { DeleteOutlineOutlined, HowToRegOutlined } from "@mui/icons-material";
import SearchIcon from "@mui/icons-material/Search";
import { Avatar, Box, Button, Card, Chip, CircularProgress, IconButton, InputAdornment, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, TextField, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import { useModals } from "../../../context/ModalContext";
import { useGlobalSnackbar } from "../../../context/SnackbarContext";
import { BaseModalComponent } from "../../common/BaseModalComponent";
import { CustomFabModal } from "../../shared/components/FABs/CustomFabModal";
import { colors, alphaVar } from "../../ui/colors";
import { WorkforceCreateView } from "../../workforce/views/WorkforceCreateView";
import { useWorkforceDeleteMutation } from "../hooks/useDeleteWorkforceMutation";
import { useWorkforceOverview } from "../hooks/useWorkforceOverview";
import { useUpdateWorkforce } from "../WorkforceInfoView/components/hooks/useWorkForce";
import { WorkforceDetail } from "./WorkforceDetail";
import { formatUSPhone, getRecordEmail, getRecordPhone, invalidateContactsAndWorkforces } from "../../../utils";

const getWorkforceDockLabel = (workforce: any, getWorkforceName: (member: any) => string) => {
  if (!workforce) return "Workforce Details";

  const name = getWorkforceName(workforce);
  if (name && name !== "Unknown") return `Workforce — ${name}`;

  return workforce.uid ? `Workforce ${String(workforce.uid).slice(0, 12)}` : "Workforce Details";
};

export const DetailedWorkforceOverview = ({
  contact = null,
  onLinkContact = null,
  initialFilter = "all",
  hideFilterToggle = false,
}: {
  contact?: any;
  onLinkContact?: (member: any) => void;
  initialFilter?: any;
  hideFilterToggle?: boolean;
}) => {
  const queryClient = useQueryClient();

  const {
    theme,
    searchValue,
    setSearchValue,
    filterType,
    page,
    setPage,
    pageSize,
    setPageSize,
    detailsWorkforce,
    detailsTriggerRef,
    loading,
    totalItems,
    paginatedWorkforces,
    formatDate,
    getWorkforceName,
    getWorkforceTypeLabel,
    getWorkforceTypeColor,
    handleWorkforceClick,
    handleFilterChange,
    closeByKey,
  } = useWorkforceOverview(initialFilter);

  const isApplicantFilter = initialFilter === "applicant" || filterType === "applicant";

  const prePopulatedDefaultValues = useMemo(() => {
    if (!contact) {
      return isApplicantFilter ? { status: "APPLICANT" } : { status: "ACTIVE" };
    }

    const digits = contact.phoneNumbers?.[0]?.number?.replace(/\D/g, "") || "";
    let dialCode = "+1";
    let phone = digits;
    if (digits.startsWith("1") && digits.length === 11) {
      phone = digits.substring(1);
    }

    return {
      firstName: contact.name || "",
      lastName: contact.lastName || "",
      emails: contact.email ? [{ value: contact.email }] : [{ value: "" }],
      phones: digits ? [{ countryCode: "US", dialCode, phone }] : [{ countryCode: "US", dialCode, phone: "" }],
      status: contact.type === "JOB_APPLICANT" || isApplicantFilter ? "APPLICANT" : "ACTIVE",
    };
  }, [contact, initialFilter, filterType, isApplicantFilter]);

  const { showDialog } = useModals();
  const { showSnackbar } = useGlobalSnackbar();
  const { mutate: deleteWorkforce } = useWorkforceDeleteMutation();
  const updateWorkforceMutation = useUpdateWorkforce();

  return (
    <Box sx={contact ? { p: 3 } : {}}>
      {/* Header Controls */}
      <Box
        sx={{
          mb: 3,
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          alignItems: { xs: "stretch", md: "center" },
          justifyContent: "space-between",
          gap: 2,
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontWeight: 800,
            color: theme.palette.text.primary,
            fontSize: "1.125rem",
            letterSpacing: "-0.02em",
          }}
        >
          {isApplicantFilter ? "Detailed Applicant Overview" : "Detailed Workforce Overview"}
        </Typography>

        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            alignItems: { xs: "stretch", sm: "center" },
            gap: 2,
          }}
        >
          <TextField
            size="small"
            placeholder={isApplicantFilter ? "Search applicants..." : "Search workforce..."}
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            autoComplete="off"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: theme.palette.text.secondary, fontSize: 18, ml: 0.5 }} />
                </InputAdornment>
              ),
            }}
            sx={{
              width: { xs: "100%", md: 240 },
              "& .MuiOutlinedInput-root": {
                borderRadius: "12px",
                bgcolor: alphaVar(theme.palette.grey[100], 0.4),
                fontSize: "0.875rem",
                "& fieldset": { borderColor: "transparent" },
                "&:hover fieldset": { borderColor: alphaVar(theme.palette.divider, 0.5) },
                "&.Mui-focused fieldset": { borderColor: colors.orange[400], borderWidth: 1.5 },
                "&.Mui-focused": { bgcolor: "var(--fx-surface-base, #FFFFFF)" },
                transition: "all 0.2s ease",
              },
            }}
          />

          {!hideFilterToggle && (
            <ToggleButtonGroup
              value={filterType}
              exclusive
              onChange={handleFilterChange}
              sx={{
                display: "flex",
                p: 0.5,
                bgcolor: alphaVar(theme.palette.grey[100], 0.6),
                borderRadius: "12px",
                gap: 0.5,
                border: "none",
                alignSelf: "center",
                "& .MuiToggleButtonGroup-grouped": {
                  border: 0,
                  "&.Mui-disabled": { border: 0 },
                  "&:not(:first-of-type), &:first-of-type": { borderRadius: "10px" },
                },
              }}
            >
              {[
                { label: "All", value: "all" },
                { label: "Supervisor", value: "supervisor" },
                { label: "Contractor", value: "contractor" },
                { label: "Employee", value: "employee" },
                { label: "Applicants", value: "applicant" },
              ].map((btn) => (
                <ToggleButton
                  key={btn.value}
                  value={btn.value}
                  sx={{
                    px: 2,
                    py: 0.75,
                    fontSize: "0.75rem",
                    fontWeight: 700,
                    textTransform: "none",
                    color: theme.palette.text.secondary,
                    transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                    "&.Mui-selected": {
                      bgcolor: "var(--fx-surface-base, #FFFFFF)",
                      color: colors.orange[400],
                      boxShadow: "0px 2px 8px rgba(0,0,0,0.06)",
                      "&:hover": { bgcolor: "var(--fx-surface-base, #FFFFFF)" },
                    },
                    "&:hover": {
                      bgcolor: alphaVar(theme.palette.common.white, 0.5),
                      color: colors.orange[400],
                    },
                  }}
                >
                  {btn.label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          )}
        </Box>
      </Box>

      {/* Table Section */}
      <Card
        sx={{
          borderRadius: 3,
          border: `1px solid ${theme.palette.divider}`,
          boxShadow: "0 4px 20px -2px rgba(0, 0, 0, 0.05), 0 2px 10px -2px rgba(0, 0, 0, 0.03)",
          bgcolor: "var(--fx-surface-base, #FFFFFF)",
          overflow: "hidden",
          mb: 1,
          width: "100%",
        }}
      >
        <TableContainer sx={{ maxHeight: 640, overflow: "hidden" }}>
          <Table
            stickyHeader
            sx={{
              tableLayout: "fixed",
              width: "100%",
            }}
          >
            <TableHead>
              <TableRow>
                {[
                  { label: "Date", width: { xs: "0%", md: "15%" }, ideMobile: true },
                  { label: "Member", width: { xs: "50%", md: "20%" }, ideMobile: false },
                  { label: "Type", width: { xs: "30%", md: "15%" }, ideMobile: false },
                  { label: "Email", width: { xs: "0%", md: "15%" }, ideMobile: true },
                  { label: "Phone", width: { xs: "0%", md: "15%" }, ideMobile: true },
                  { label: contact ? "Link" : "", width: { xs: "20%", md: contact ? "12%" : "7%" } },
                ].map((col) => (
                  <TableCell
                    key={col.label}
                    sx={{
                      bgcolor: alphaVar(theme.palette.grey[50], 0.8),
                      fontSize: "0.7rem",
                      fontWeight: 800,
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                      color: theme.palette.text.secondary,
                      py: 1,
                      width: col.width,
                      display: col.ideMobile ? { xs: "none", md: "table-cell" } : "table-cell",
                      borderBottom: `2px solid ${theme.palette.divider}`,
                    }}
                  >
                    {col.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 12 }}>
                    <CircularProgress size={32} thickness={5} sx={{ color: colors.orange[400] }} />
                  </TableCell>
                </TableRow>
              ) : paginatedWorkforces.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 12 }}>
                    <Typography variant="body2" sx={{ color: theme.palette.text.secondary, fontWeight: 500 }}>
                      {searchValue ? "No workforce members match your search criteria" : "Your workforce portfolio is currently empty"}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedWorkforces.map((workforce: any) => {
                  const typeLabel = getWorkforceTypeLabel(workforce.workforceRole || workforce.role || workforce.type);
                  const typeColor = getWorkforceTypeColor(workforce.workforceRole || workforce.role || workforce.type);
                  const memberName = getWorkforceName(workforce);
                  const initials = `${workforce.firstName?.[0] || ""}${workforce.lastName?.[0] || ""}`.toUpperCase();

                  return (
                    <TableRow
                      key={workforce.uid}
                      onClick={() => !contact && handleWorkforceClick(workforce)}
                      sx={{
                        cursor: contact ? "default" : "pointer",
                        "&:hover": contact ? {} : { bgcolor: alphaVar(theme.palette.grey[50], 0.7) },
                        transition: "all 0.2s ease",
                      }}
                    >
                      <TableCell
                        sx={{
                          display: { xs: "none", md: "table-cell" },
                          py: 2.5,
                          whiteSpace: "nowrap",
                          fontSize: "0.875rem",
                          color: theme.palette.text.secondary,
                        }}
                      >
                        {formatDate(workforce.createdAt)}
                      </TableCell>

                      <TableCell>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, overflow: "hidden" }}>
                          <Avatar
                            src={workforce.avatar}
                            sx={{
                              width: 30,
                              height: 30,
                              bgcolor: alphaVar(typeColor, 0.1),
                              color: typeColor,
                              fontSize: "0.75rem",
                              fontWeight: 800,
                              flexShrink: 0,
                            }}
                          >
                            {initials}
                          </Avatar>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 700,
                              color: theme.palette.text.primary,
                              fontSize: "0.875rem",
                              textOverflow: "ellipsis",
                              overflow: "hidden",
                            }}
                          >
                            {memberName}
                          </Typography>
                        </Box>
                      </TableCell>

                      <TableCell>
                        <Chip
                          label={typeLabel}
                          size="small"
                          sx={{
                            height: 22,
                            fontSize: "0.625rem",
                            fontWeight: 800,
                            bgcolor: alphaVar(typeColor, 0.08),
                            color: typeColor,
                            textTransform: "uppercase",
                            borderRadius: "6px",
                          }}
                        />
                      </TableCell>

                      <TableCell
                        sx={{
                          fontSize: "0.8125rem",
                          color: theme.palette.text.secondary,
                          display: { xs: "none", md: "table-cell" },
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {getRecordEmail(workforce) || "—"}
                      </TableCell>

                      <TableCell sx={{ fontSize: "0.8125rem", color: theme.palette.text.secondary, display: { xs: "none", md: "table-cell" } }}>
                        {getRecordPhone(workforce, { formatted: true }) || "—"}
                      </TableCell>

                      <TableCell
                        align="right"
                        sx={{
                          py: contact ? 1 : 2,
                          px: 2,
                          display: contact ? "table-cell" : { xs: "none", md: "table-cell" },
                          whiteSpace: "nowrap",
                        }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Box
                          sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "flex-end",
                            gap: 1.5,
                          }}
                        >
                          {contact ? (
                            <Button
                              variant="contained"
                              size="small"
                              onClick={() => {
                                const phoneRaw = contact?.phoneNumbers?.[0]?.number || contact?.phoneNumber || "";
                                const phoneFormatted = phoneRaw ? formatUSPhone(phoneRaw) : "";

                                showDialog({
                                  type: "confirm",
                                  title: "Link Phone Number",
                                  message: (
                                    <>
                                      Are you sure you want to add phone number <strong>{phoneFormatted}</strong> to workforce member{" "}
                                      <strong>{memberName}</strong>?
                                    </>
                                  ),
                                  confirmLabel: "Confirm",
                                  cancelLabel: "Cancel",
                                  onConfirm: () => {
                                    onLinkContact?.(workforce);
                                  },
                                });
                              }}
                              sx={{
                                textTransform: "none",
                                fontWeight: 600,
                                fontSize: "0.75rem",
                                px: 2,
                                py: 0.6,
                                borderRadius: "20px",
                                bgcolor: colors.orange[400],
                                color: "#fff",
                                "&:hover": {
                                  bgcolor: colors.orange[600],
                                },
                              }}
                            >
                              Select
                            </Button>
                          ) : (
                            <IconButton
                              size="small"
                              onClick={async (e) => {
                                e.stopPropagation();
                                showDialog({
                                  type: "confirm",
                                  title: "Delete Member?",
                                  message: "Are you sure you want to delete this workforce member?",
                                  confirmLabel: "Yes, delete",
                                  cancelLabel: "Cancel",
                                  onConfirm: async () => {
                                    deleteWorkforce(workforce.uid, {
                                      onSuccess: () => showSnackbar(`${memberName} has been deleted.`),
                                      onError: (error: any) => showSnackbar(error?.response?.data?.message || error?.message || "Something went wrong"),
                                    });
                                  },
                                });
                              }}
                              sx={{
                                p: 0.75,
                                color: theme.palette.text.secondary,
                                "&:hover": {
                                  color: colors.danger?.[600] || "#d32f2f",
                                  bgcolor: colors.danger?.[100] || alphaVar("#d32f2f", 0.08),
                                },
                              }}
                            >
                              <DeleteOutlineOutlined sx={{ fontSize: 18 }} />
                            </IconButton>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {paginatedWorkforces.length > 0 && (
          <Box
            sx={{
              px: { xs: 2, sm: 3 },
              py: 2,
              bgcolor: alphaVar(theme.palette.grey[50], 0.5),
              borderTop: `1px solid ${theme.palette.divider}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Typography variant="body2" sx={{ fontSize: "0.75rem", color: theme.palette.text.secondary }}>
              {Math.min(page * pageSize + 1, totalItems)} – {Math.min((page + 1) * pageSize, totalItems)} of {totalItems}
            </Typography>
            <TablePagination
              component="div"
              count={totalItems}
              page={page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={pageSize}
              onRowsPerPageChange={(e) => {
                setPageSize(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[5, 10, 25]}
              sx={{
                "& .MuiTablePagination-toolbar": { padding: 0 },
                "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": { display: "none" },
              }}
            />
          </Box>
        )}
      </Card>

      {/* FAB - Using Custom Styles */}
      <CustomFabModal
        directions={{ bottom: 40, right: 40 }}
        tooltip={isApplicantFilter ? "Create New Applicant" : "Create New Workforce Member"}
        modalKey="Dashboard-Workforce-Create-Overview"
        modalLabel={isApplicantFilter ? "Add Applicant" : "Add Member"}
        size={undefined}
        icon={undefined}
        allowInsidePreventModal={true}
      >
        <WorkforceCreateView
          defaultValues={prePopulatedDefaultValues}
          modalKey="Dashboard-Workforce-Create-Overview"
          onSuccess={async () => {
            await queryClient.invalidateQueries({ queryKey: ["workforces"] });
            await queryClient.invalidateQueries({ queryKey: ["total-workforce"] });
            await invalidateContactsAndWorkforces(queryClient);
          }}
        />
      </CustomFabModal>

      {/* Details Modal */}
      <BaseModalComponent
        modalKey={detailsWorkforce?.uid ? `Workforce-Portfolio-Details-${detailsWorkforce.uid}` : "Workforce-Portfolio-Details-Modal"}
        modalLabel={getWorkforceDockLabel(detailsWorkforce, getWorkforceName)}
        elementTrigger={<button ref={detailsTriggerRef} type="button" style={{ position: "absolute", visibility: "hidden" }} />}
        // @ts-ignore
        sx={{ zIndex: 99999 }}
      >
        {detailsWorkforce && (
          <WorkforceDetail
            workforce={detailsWorkforce}
            onRoleChanged={() => {}} // update handled internally by cache
          />
        )}
      </BaseModalComponent>
    </Box>
  );
};
